import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const TrustIndicatorSection = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const certifications = [
    { name: t('trustIndicators.fifa'), description: t('trustIndicators.licensedIntermediary'), icon: 'Award', verified: true, gradient: 'from-blue-600 to-blue-700' },
    { name: t('trustIndicators.caf'), description: t('trustIndicators.approvedAgent'), icon: 'Shield', verified: true, gradient: 'from-emerald-600 to-emerald-700' },
    { name: t('trustIndicators.fa'), description: t('trustIndicators.registeredRepresentative'), icon: 'CheckCircle', verified: true, gradient: 'from-purple-600 to-purple-700' },
    { name: t('trustIndicators.uefa'), description: t('trustIndicators.accreditedPartner'), icon: 'Star', verified: true, gradient: 'from-amber-600 to-amber-700' }
  ];

  const partnerships = [
    { name: 'Adidas', category: 'Sportswear', verified: true },
    { name: 'Nike', category: 'Sportswear', verified: true },
    { name: 'Puma', category: 'Sportswear', verified: true },
    { name: 'Under Armour', category: 'Sportswear', verified: true },
    { name: 'New Balance', category: 'Sportswear', verified: true },
    { name: 'Reebok', category: 'Sportswear', verified: true },
    { name: 'Umbro', category: 'Sportswear', verified: true },
    { name: 'Kappa', category: 'Sportswear', verified: true },
    { name: 'SAP', category: 'Technology', verified: true },
    { name: 'Microsoft', category: 'Technology', verified: true },
    { name: 'AWS', category: 'Technology', verified: true },
    { name: 'Oracle', category: 'Technology', verified: true },
    { name: 'Visa', category: 'Financial', verified: true },
    { name: 'Mastercard', category: 'Financial', verified: true },
    { name: 'PayPal', category: 'Financial', verified: true },
    { name: 'American Express', category: 'Financial', verified: true },
    { name: 'Emirates', category: 'Travel', verified: true },
    { name: 'Qatar Airways', category: 'Travel', verified: true },
    { name: 'Etihad Airways', category: 'Travel', verified: true },
    { name: 'Turkish Airlines', category: 'Travel', verified: true },
    { name: 'Garmin', category: 'Sports Tech', verified: true },
    { name: 'Polar', category: 'Sports Tech', verified: true },
    { name: 'Catapult', category: 'Sports Tech', verified: true },
    { name: 'StatSports', category: 'Sports Tech', verified: true },
    { name: 'ESPN', category: 'Media', verified: true },
    { name: 'Sky Sports', category: 'Media', verified: true },
    { name: 'BeIN Sports', category: 'Media', verified: true }
  ];

  const mediaRecognition = [
    { outlet: 'BBC Sport', quote: 'Revolutionary approach to African talent management', author: 'John Smith', role: 'Football Correspondent' },
    { outlet: 'Sky Sports', quote: 'Setting new standards in player representation', author: 'Sarah Johnson', role: 'Transfer Expert' },
    { outlet: 'ESPN', quote: 'Bridging continents with professionalism and integrity', author: 'Michael Brown', role: 'Senior Analyst' }
  ];

  const partnershipCategories = [
    { name: t('trustIndicators.sportswear'), count: partnerships?.filter(p => p?.category === 'Sportswear')?.length, icon: 'Shirt', gradient: 'from-blue-600 to-blue-700' },
    { name: t('trustIndicators.technology'), count: partnerships?.filter(p => p?.category === 'Technology')?.length, icon: 'Cpu', gradient: 'from-emerald-600 to-emerald-700' },
    { name: t('trustIndicators.financial'), count: partnerships?.filter(p => p?.category === 'Financial')?.length, icon: 'CreditCard', gradient: 'from-purple-600 to-purple-700' },
    { name: t('trustIndicators.travel'), count: partnerships?.filter(p => p?.category === 'Travel')?.length, icon: 'Plane', gradient: 'from-amber-600 to-amber-700' },
    { name: t('trustIndicators.sportsTech'), count: partnerships?.filter(p => p?.category === 'Sports Tech')?.length, icon: 'Activity', gradient: 'from-pink-600 to-pink-700' },
    { name: t('trustIndicators.media'), count: partnerships?.filter(p => p?.category === 'Media')?.length, icon: 'Tv', gradient: 'from-indigo-600 to-indigo-700' }
  ];

  return (
    <section className="py-16 md:py-24 lg:py-32 bg-gradient-to-b from-slate-50 to-white relative overflow-hidden">
      <div className="absolute inset-0 opacity-5" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, rgba(0,0,0,0.15) 1px, transparent 0)', backgroundSize: '32px 32px' }} />

      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-blue-100 to-emerald-100 rounded-full mb-6 shadow-sm">
            <Icon name="Shield" size={18} className="text-blue-600" />
            <span className="text-sm font-bold text-slate-800">{t('trustIndicators.trustCredibility')}</span>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-6xl font-black text-slate-900 mb-6 leading-tight">
            <span className="bg-gradient-to-r from-blue-600 via-emerald-600 to-blue-600 bg-clip-text text-transparent">
              {t('trustIndicators.fifaCertifiedExcellence')}
            </span>
          </h2>
          <p className="text-lg md:text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
            {t('trustIndicators.regulatoryCompliance')}
          </p>
        </div>

        {/* EA Sports FC + PS5 Gaming Partners Banner */}
        <div className="mb-16 rounded-3xl overflow-hidden" style={{ background: 'linear-gradient(135deg, #060810 0%, #0d1b3e 50%, #081a10 100%)', border: '1px solid rgba(255,255,255,0.08)' }}>
          {/* Field lines decoration */}
          <div className="relative overflow-hidden">
            <div className="absolute inset-0 pointer-events-none">
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 rounded-full" style={{ border: '1px solid rgba(255,255,255,0.04)' }} />
              <div className="absolute inset-0" style={{ backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 50px, rgba(255,255,255,0.015) 50px, rgba(255,255,255,0.015) 51px)' }} />
            </div>
            <div className="relative px-8 md:px-12 py-10">
              <div className="text-center mb-8">
                <span className="text-xs font-bold uppercase tracking-[0.3em]" style={{ color: 'rgba(255,255,255,0.35)' }}>Official Gaming &amp; Technology Partners</span>
                <h3 className="text-2xl md:text-3xl font-black text-white mt-2">Powered by the World&apos;s Best Gaming Platforms</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* EA Sports FC Card */}
                <div className="rounded-2xl p-6 flex items-center gap-6" style={{ background: 'rgba(255,107,0,0.08)', border: '1px solid rgba(255,107,0,0.25)' }}>
                  <div className="flex-shrink-0">
                    <svg viewBox="0 0 200 75" fill="none" xmlns="http://www.w3.org/2000/svg" width="170" height="64">
                      <text x="0" y="26" fontFamily="Arial Black, sans-serif" fontSize="16" fontWeight="900" fill="#FF6B00" letterSpacing="2">EA SPORTS</text>
                      <text x="0" y="68" fontFamily="Arial Black, sans-serif" fontSize="52" fontWeight="900" fill="white" letterSpacing="2">FC</text>
                      <rect x="68" y="44" width="132" height="4" fill="#FF6B00"/>
                      <text x="72" y="66" fontFamily="Arial, sans-serif" fontSize="16" fontWeight="700" fill="#FF6B00" letterSpacing="4">24/25</text>
                    </svg>
                  </div>
                  <div>
                    <p className="text-white font-bold text-sm mb-1">EA Sports FC 24 &amp; 25/26</p>
                    <p className="text-xs leading-relaxed" style={{ color: 'rgba(255,255,255,0.45)' }}>Official gaming partner bringing immersive football experiences to life</p>
                    <div className="flex gap-2 mt-3">
                      <span className="text-xs px-2.5 py-1 rounded-full font-bold" style={{ background: 'rgba(255,107,0,0.2)', color: '#FF9500', border: '1px solid rgba(255,107,0,0.3)' }}>FC 24</span>
                      <span className="text-xs px-2.5 py-1 rounded-full font-bold" style={{ background: 'rgba(255,107,0,0.2)', color: '#FF9500', border: '1px solid rgba(255,107,0,0.3)' }}>FC 25/26</span>
                    </div>
                  </div>
                </div>
                {/* PS5 Card */}
                <div className="rounded-2xl p-6 flex items-center gap-6" style={{ background: 'rgba(0,52,143,0.15)', border: '1px solid rgba(0,100,255,0.25)' }}>
                  <div className="flex-shrink-0">
                    <svg viewBox="0 0 160 75" fill="none" xmlns="http://www.w3.org/2000/svg" width="130" height="61">
                      <text x="0" y="65" fontFamily="Arial Black, sans-serif" fontSize="65" fontWeight="900" fill="white" letterSpacing="-2">PS5</text>
                      <circle cx="140" cy="22" r="12" fill="none" stroke="#E8003D" strokeWidth="2.5"/>
                      <rect x="128" y="38" width="24" height="16" rx="3" fill="#003791" stroke="rgba(255,255,255,0.3)" strokeWidth="1"/>
                      <polygon points="132,38 148,38 140,30" fill="#003791"/>
                    </svg>
                  </div>
                  <div>
                    <p className="text-white font-bold text-sm mb-1">PlayStation 5 &mdash; Marketing Partner</p>
                    <p className="text-xs leading-relaxed" style={{ color: 'rgba(255,255,255,0.45)' }}>Specialized immersive experiences with Haptic Feedback &amp; 3D Audio</p>
                    <div className="flex gap-2 mt-3">
                      <span className="text-xs px-2.5 py-1 rounded-full font-bold" style={{ background: 'rgba(0,52,143,0.4)', color: '#6699ff', border: '1px solid rgba(0,100,255,0.3)' }}>Haptic Feedback</span>
                      <span className="text-xs px-2.5 py-1 rounded-full font-bold" style={{ background: 'rgba(0,52,143,0.4)', color: '#6699ff', border: '1px solid rgba(0,100,255,0.3)' }}>3D Audio</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Certifications & Media Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 mb-16 md:mb-20">
          <div className="bg-white border-2 border-slate-200 rounded-3xl p-8 md:p-10 shadow-xl">
            <h3 className="text-2xl md:text-3xl font-black text-slate-900 mb-8">{t('trustIndicators.officialCertifications')}</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
              {certifications?.map((cert, index) => (
                <div key={index} className="group bg-gradient-to-br from-slate-50 to-slate-100 border-2 border-slate-200 rounded-2xl p-5 hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
                  <div className="flex items-start gap-3">
                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${cert?.gradient} flex items-center justify-center flex-shrink-0 shadow-md group-hover:scale-110 transition-transform duration-300`}>
                      <Icon name={cert?.icon} size={24} color="white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <h4 className="font-black text-slate-900 text-sm">{cert?.name}</h4>
                        {cert?.verified && <Icon name="BadgeCheck" size={16} className="text-emerald-600" />}
                      </div>
                      <p className="text-xs text-slate-600 font-medium">{cert?.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <Button variant="default" fullWidth iconName="FileText" iconPosition="right" onClick={() => navigate('/compliance-center')} className="bg-gradient-to-r from-blue-600 to-emerald-600 hover:from-blue-700 hover:to-emerald-700 text-white shadow-lg border-0 font-bold py-4">
              {t('trustIndicators.viewAllCertifications')}
            </Button>
          </div>

          <div className="bg-white border-2 border-slate-200 rounded-3xl p-8 md:p-10 shadow-xl">
            <h3 className="text-2xl md:text-3xl font-black text-slate-900 mb-8">{t('trustIndicators.mediaRecognition')}</h3>
            <div className="space-y-6">
              {mediaRecognition?.map((media, index) => (
                <div key={index} className="bg-gradient-to-br from-slate-50 to-slate-100 border-2 border-slate-200 rounded-2xl p-6 hover:shadow-lg transition-all duration-300">
                  <div className="flex items-start gap-4 mb-4">
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-600 to-emerald-600 flex items-center justify-center flex-shrink-0">
                      <Icon name="Quote" size={20} color="white" />
                    </div>
                    <p className="text-base text-slate-700 italic font-medium leading-relaxed">&ldquo;{media?.quote}&rdquo;</p>
                  </div>
                  <div className="flex items-center justify-between pl-14">
                    <div>
                      <div className="font-black text-sm text-slate-900">{media?.author}</div>
                      <div className="text-xs text-slate-600 font-semibold">{media?.role}</div>
                    </div>
                    <div className="text-xs font-black text-blue-600 bg-blue-50 px-3 py-1.5 rounded-lg">{media?.outlet}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Strategic Partners Section */}
        <div className="bg-gradient-to-br from-blue-50 via-white to-emerald-50 rounded-3xl p-8 md:p-12 lg:p-16 border-2 border-slate-200 mb-12 shadow-xl">
          <div className="text-center mb-12">
            <h3 className="text-3xl md:text-4xl lg:text-5xl font-black text-slate-900 mb-4">{t('trustIndicators.strategicPartners')}</h3>
            <p className="text-base md:text-lg text-slate-600 max-w-2xl mx-auto">{t('trustIndicators.collaboratingWithBrands')}</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-6 mb-10">
            {partnershipCategories?.map((category, index) => (
              <div key={index} className="bg-white border-2 border-slate-200 rounded-2xl p-6 text-center hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
                <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${category?.gradient} flex items-center justify-center mx-auto mb-4 shadow-lg`}>
                  <Icon name={category?.icon} size={28} color="white" />
                </div>
                <h4 className="font-black text-sm text-slate-900 mb-1">{category?.name}</h4>
                <p className="text-xs text-slate-600 font-semibold">{category?.count} {t('trustIndicators.partners')}</p>
              </div>
            ))}
          </div>
          <div className="bg-white border-2 border-slate-200 rounded-2xl p-8">
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {partnerships?.map((partner, index) => (
                <div key={index} className="bg-gradient-to-br from-slate-50 to-slate-100 border border-slate-200 rounded-xl p-4 hover:shadow-md transition-all duration-300 text-center group">
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <span className="text-sm font-black text-slate-900 group-hover:text-blue-600 transition-colors">{partner?.name}</span>
                    {partner?.verified && <Icon name="CheckCircle" size={14} className="text-emerald-600" />}
                  </div>
                  <p className="text-xs text-slate-600 font-semibold">{partner?.category}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button variant="default" size="lg" iconName="Globe" iconPosition="left" onClick={() => navigate('/partnership-network')} className="bg-gradient-to-r from-blue-600 to-emerald-600 hover:from-blue-700 hover:to-emerald-700 text-white shadow-xl border-0 font-bold px-10 py-6">
            {t('trustIndicators.explorePartnershipNetwork')}
          </Button>
          <Button variant="outline" size="lg" iconName="FileText" iconPosition="left" onClick={() => navigate('/compliance-center')} className="border-2 border-slate-300 text-slate-900 hover:bg-slate-100 font-bold px-10 py-6">
            {t('trustIndicators.viewComplianceDocuments')}
          </Button>
        </div>
      </div>
    </section>
  );
};

export default TrustIndicatorSection;